import { SeekPassIconVerified } from '@seek/certsy-ui';
import { useTranslations } from '@vocab/react';
import {
  Accordion,
  AccordionItem,
  ButtonLink,
  Divider,
  Heading,
  IconNewWindow,
  Inline,
  Stack,
  Text,
  Tiles,
} from 'braid-design-system';
import { useEffect } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import { useAppConfig } from 'src/modules/appConfig';
import { useSeekPassUrl } from 'src/modules/hooks/useSeekPassUrl';
import {
  CredentialExpiryStatus,
  type VerifiedCredential2,
} from 'src/types/graphql';

import { useVerifications } from '../data';
import { useGetLicenceExpiryStatusBadge } from '../useGetLicenceExpiryStatusBadge/useGetLicenceExpiryStatusBadge';

import translations from './.vocab';
import { SearchSvg } from './SearchSvg';

const VerifiedCredentialItem = ({
  credential,
}: {
  credential: VerifiedCredential2;
}) => {
  const { t } = useTranslations(translations);
  const { label, credentialInfo, expiryStatus } = credential;

  const badge = useGetLicenceExpiryStatusBadge(expiryStatus);

  return credentialInfo.length > 0 ? (
    // If credential has expandable info, display as an accordion
    <Accordion
      size="standard"
      tone="secondary"
      space="xlarge"
      weight="regular"
      dividers={false}
    >
      <AccordionItem
        label={label}
        id="verified_credential_accordian_item"
        icon={<SeekPassIconVerified />}
        badge={badge}
      >
        <Stack space="medium">
          <Tiles columns={2} space="medium">
            {credentialInfo.map(({ name, values }, credentialIndex) => (
              <Stack space="xsmall" key={credentialIndex}>
                <Text size="xsmall" tone="secondary">
                  {name}
                </Text>
                <Stack space="small">
                  {values.map((value, valueIndex) => (
                    <Text size="standard" tone="secondary" key={valueIndex}>
                      {value}
                    </Text>
                  ))}
                </Stack>
              </Stack>
            ))}
          </Tiles>
          {expiryStatus === CredentialExpiryStatus.Expired && (
            <Text size="xsmall" tone="critical">
              {t('Only you can see verifications that have expired.')}
            </Text>
          )}
        </Stack>
      </AccordionItem>
    </Accordion>
  ) : (
    <Text icon={<SeekPassIconVerified />} tone="secondary">
      {label}
    </Text>
  );
};

export const YourVerifications = () => {
  const { t } = useTranslations(translations);

  const { verificationsDrawerOpened, seekPassManageClicked } = useAnalytics();

  const { verified, verificationsTrackingProps, loading, error } =
    useVerifications();
  const isLoading = Boolean(loading || error);

  const { brand } = useAppConfig();

  const seekPassUrl = useSeekPassUrl(
    'https://app.seekpass.co/career-passport/recommended',
  );

  useEffect(() => {
    const { credentialsVerifiedTotal, credentialsVerifiedType } =
      verificationsTrackingProps;

    verificationsDrawerOpened({
      credentialsVerifiedTotal,
      credentialsVerifiedType,
      currentPageSubsection: 'your verifications',
    });
    // Only send tracking once on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return null;
  }

  const emptyContent = (
    <Stack space="medium" align="center">
      <SearchSvg />
      <Heading level="4">{t('You don’t have any verifications yet')}</Heading>
      <Text align="center">
        {t(
          'Look for what is available to verify to add more trust to your BRAND Profile.',
          { brand },
        )}
      </Text>
    </Stack>
  );

  return verified && verified.length > 0 ? (
    <Stack space="xlarge">
      <Stack space="xlarge">
        {verified.map(({ label, credentials }, sectionIndex) => (
          <Stack space="large" key={sectionIndex}>
            <Text weight="strong">{label}</Text>
            <Stack space="xlarge">
              {credentials
                ? credentials.map((credential, credentialIndex) => (
                    <VerifiedCredentialItem
                      key={credentialIndex}
                      credential={credential}
                    />
                  ))
                : null}
            </Stack>
          </Stack>
        ))}
      </Stack>
      <Divider />
      <Stack space="medium">
        <Text size="large" weight="strong">
          {t('Manage verifications')}
        </Text>
        <Text>{t('Update and manage your verification on SEEK Pass.')}</Text>
        <Inline space="none">
          <ButtonLink
            icon={<IconNewWindow />}
            iconPosition="trailing"
            target="_blank"
            onClick={seekPassManageClicked}
            href={seekPassUrl}
          >
            {t('Manage on SEEK Pass')}
          </ButtonLink>
        </Inline>
      </Stack>
    </Stack>
  ) : (
    emptyContent
  );
};
